import { styled } from "styled-components";
import { useEffect } from "react";
import { IdentityVerificationsManualUploadSection } from "./IdentityVerificationsManualUploadSection";
import { IdentityVerificationsOpenAdminSection } from "./IdentityVerificationsOpenAdminSection";
import { useBrowserLocalStorage } from "src/apps/hooks/useBrowserLocalStorage";
import { useDocumentationAreaIdAndActorId } from "src/apps/hooks/useDocumentationAreaIdAndActorId";
import {
  AnalyticsEvents,
  useSendAnalyticEvent,
} from "src/apps/hooks/analytics/useSendAnalyticEvent";
import { OrderIdentityVerification } from "src/contexts/documentationArea/domain/OrderIdentityVerification";
import { IdentityVerificationManualUploadState } from "./IdentityVerificationManualUploadState";
import { MEDIA_MOBILE_BREAKPOINT } from "src/apps/config/constants";
import { TextContent } from "src/apps/components/typography/Texts";
import { T } from "src/apps/components/i18n/Translation";
import { Icon, ICON_SIZES } from "src/apps/components/icon/Icon";
import { ICON_PATHS } from "src/apps/components/icon/IconSVGPaths";

import { useDocumentationArea } from "src/apps/hooks/useDocumentationArea";
import { colors } from "@permisso/permisso-components";

const COMPANY_REPRESENTATIVE_HINT = "documentation_area.uploader_box.company_representative_hint";

interface IdentityVerificationOptionsProps {
  order: OrderIdentityVerification;
}

export const IdentityVerificationOptions = ({ order }: IdentityVerificationOptionsProps) => {
  const { documentationAreaId } = useDocumentationAreaIdAndActorId();
  const [selectedIdentityVerificationMethod, setIdentityVerificationMethod] =
    useBrowserLocalStorage<string | null>(
      `selected_identity_verification_method.${documentationAreaId}`,
      null,
    );
  const { isCompany, representativeIdNumber } = useDocumentationArea();
  const { sendEvent } = useSendAnalyticEvent();

  const isCompleted = order.hasValidResponses();
  const hasErrors = order.hasResponses() && !order.hasValidResponses();
  const openAdminMethod = order
    .getRetrievalMethods()
    .find((method) => method.isAutomaticExtraction());
  const manualUploadMethod = order.getRetrievalMethods().find((method) => method.isManualUpload());

  const selectIdentityVerificationMethod = (method: "open_admin" | "manual_upload") => {
    setIdentityVerificationMethod(method);
    switch (method) {
      case "open_admin":
        sendEvent(AnalyticsEvents.DOCUMENTATION_AREA_OPEN_ADMIN_IDV_CLICKED);
        break;
      case "manual_upload":
        sendEvent(AnalyticsEvents.DOCUMENTATION_AREA_MANUAL_IDV_CLICKED);
        break;
    }
  };

  useEffect(() => {
    if (selectedIdentityVerificationMethod === "open_admin" && !openAdminMethod) {
      setIdentityVerificationMethod(null);
      return;
    }

    if (selectedIdentityVerificationMethod === "manual_upload" && !manualUploadMethod) {
      setIdentityVerificationMethod(null);
      return;
    }
  }, [
    selectedIdentityVerificationMethod,
    setIdentityVerificationMethod,
    openAdminMethod,
    manualUploadMethod,
  ]);

  const computeManualUploadState = () => {
    if (isCompleted) {
      return IdentityVerificationManualUploadState.COMPLETED;
    } else if (hasErrors) {
      return IdentityVerificationManualUploadState.ERROR;
    } else {
      return IdentityVerificationManualUploadState.NOT_COMPLETED;
    }
  };
  const companyRepresentativeHint =
    isCompany && representativeIdNumber ? (
      <CompanyRepresentativeHint className="text mp-mask">
        <Icon path={ICON_PATHS.MD_INFO} size={ICON_SIZES.s20} color={colors.grey700} />
        <T
          k={COMPANY_REPRESENTATIVE_HINT}
          params={{
            id_number: representativeIdNumber?.number,
          }}
        />
      </CompanyRepresentativeHint>
    ) : null;

  return (
    <StyledContent>
      {companyRepresentativeHint}
      {openAdminMethod && (
        <IdentityVerificationsOpenAdminSection
          isSelected={selectedIdentityVerificationMethod === "open_admin"}
          isReadonly={openAdminMethod.isReadonly()}
          onClick={() => selectIdentityVerificationMethod("open_admin")}
        />
      )}
      {manualUploadMethod && (
        <IdentityVerificationsManualUploadSection
          isSelected={selectedIdentityVerificationMethod === "manual_upload"}
          isReadonly={manualUploadMethod.isReadonly()}
          state={computeManualUploadState()}
          onClick={() => selectIdentityVerificationMethod("manual_upload")}
        />
      )}
    </StyledContent>
  );
};

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.75rem;
  @media (max-width: ${MEDIA_MOBILE_BREAKPOINT}rem) {
    padding: 1rem 0rem;
  }
`;

const CompanyRepresentativeHint = styled(TextContent)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.25rem;
  color: ${colors.grey700};
  font-size: 1rem;
  @media (max-width: ${MEDIA_MOBILE_BREAKPOINT}rem) {
    align-items: flex-start;
  }
`;
